import { useState, useRef, useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Superscript from "@tiptap/extension-superscript";
import Subscript from "@tiptap/extension-subscript";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import ListItem from "@tiptap/extension-list-item";
import Strike from "@tiptap/extension-strike";
import Code from "@tiptap/extension-code";
import CodeBlock from "@tiptap/extension-code-block";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import { TextSelection } from "prosemirror-state";
import TableHeader from "@tiptap/extension-table-header";

import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Undo2,
  Redo2,
  Link2 as LinkIcon,
  Image as ImageIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Superscript as SuperscriptIcon,
  Subscript as SubscriptIcon,
  Strikethrough,
  Code as CodeIcon,
  FileCode,
  Table as TableIcon,
  Type,
  Quote,
  Minus,
  X,
  ImagePlus,
  UploadCloud,
} from "lucide-react";
import { deleteImage, uploadImages } from "../../../services/uploaderServices";
import toast from "react-hot-toast";

const BlogModal = ({
  closeModal,
  showModal,
  setLinkURL,
  linkURL,
  updateLink,
}) => {
  return (
    <>
      {showModal && (
        <div className="fixed inset-0 bg-black/[60%] flex items-center justify-center z-50">
          <div className="w-[25%] min-w-[300px] bg-white flex justify-center items-center h-[200px] rounded-[30px] relative shadow-lg">
            <button
              className="absolute top-5 right-5 text-gray-600 hover:text-black transition"
              onClick={closeModal}
            >
              <X size={24} />
            </button>
            <div className="flex flex-col items-start w-[75%] mx-auto">
              <label className="font-semibold mt-8 text-gray-700 text-base">
                Link URL
              </label>
              <input
                onChange={(e) => setLinkURL(e.target.value)}
                value={linkURL}
                type="text"
                placeholder="https://example.com"
                className="appearance-none block w-full border border-gray-300 rounded py-2 px-4 mt-2 leading-tight focus:outline-none focus:ring-2 focus:ring-red-400"
              />
              <button
                onClick={updateLink}
                className="w-full mt-3 rounded-[10px] h-[48px] text-white font-bold text-sm bg-red-600 hover:bg-red-700 transition"
              >
                Set Link
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const BlogImagePopup = ({
  closeModal,
  showModal,
  inputRef,
  handleFileChange,
  handleInputClick,
  selectedFiles,
  handleDelete,
  handleUpload,
  uploading,
}) => {
  return (
    <>
      {showModal && (
        <div className="fixed inset-0 bg-black/[60%] flex items-center justify-center z-50">
          <div className="w-[25%] min-w-[350px] bg-white pl-8 pt-6 h-[410px] rounded-[30px] relative shadow-lg overflow-y-auto">
            <button
              className="absolute top-5 right-5 text-gray-600 hover:text-black transition"
              onClick={closeModal}
            >
              <X size={24} />
            </button>
            <div className="w-full">
              <label className="block text-base font-semibold mt-6 text-gray-700">
                Upload Image
              </label>
              <input
                ref={inputRef}
                className="hidden"
                onChange={handleFileChange}
                type="file"
                accept="image/*"
                multiple
              />
              <div className="flex justify-between items-center w-full mt-2">
                <button
                  onClick={handleInputClick}
                  className="bg-gray-200 flex items-center gap-2 text-darkblue font-semibold py-2 px-4 rounded-lg w-[195px] h-[44px] text-base"
                >
                  <ImagePlus size={18} />
                  Choose Files
                </button>
              </div>
            </div>
            <div className="w-full pt-10 pr-8">
              {selectedFiles?.length > 0 && (
                <>
                  <h1 className="text-sm font-semibold mb-2">
                    Selected Images
                  </h1>
                  <div className="flex flex-wrap gap-3">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="relative">
                        <img
                          className="w-[110px] h-[110px] rounded-lg object-cover"
                          src={URL.createObjectURL(file)}
                          alt={`image-${index}`}
                        />
                        <button
                          onClick={() => handleDelete(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={handleUpload}
                    disabled={uploading}
                    className={`bg-gray-200 mt-4 text-darkblue font-semibold py-2 px-4 rounded-lg w-[195px] h-[44px] text-base flex items-center gap-2 ${
                      uploading ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    {uploading ? (
                      "Uploading..."
                    ) : (
                      <>
                        <UploadCloud size={18} />
                        Upload Images
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export const RichTextEditor = ({ description = "", setDescription }) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [linkURL, setLinkURL] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: false,
        orderedList: false,
        listItem: false,
        code: false,
        codeBlock: false,
        strike: false,
      }),
      Underline,
      Strike,
      Code,
      CodeBlock,
      TextStyle,
      Color,
      Superscript,
      Subscript,
      BulletList.configure({
        itemTypeName: "listItem",
        HTMLAttributes: {
          class: "prose-bullet-list",
        },
      }),
      OrderedList.configure({
        itemTypeName: "listItem",
        HTMLAttributes: {
          class: "prose-ordered-list",
        },
      }),
      ListItem.configure({
        HTMLAttributes: {
          class: "list-item",
        },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          class: "text-blue-500 underline cursor-pointer",
        },
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
        alignments: ["left", "center", "right", "justify"],
      }),
      Image.configure({
        HTMLAttributes: {
          class: "max-w-full h-auto rounded-md",
        },
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: description,
    onUpdate: ({ editor }) => {
      const htmlContent = editor.getHTML();
      setDescription(htmlContent);
    },

    editorProps: {
      attributes: {
        class:
          "prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none min-h-[200px]",
      },
      handleDOMEvents: {
        keydown: (view, event) => {
          // Handle Enter key for lists
          if (event.key === "Enter") {
            const { state } = view;
            const { selection } = state;
            const { $from, empty } = selection;

            // If not at the end of a node, let the default behavior handle it
            if (!empty || $from.parentOffset < $from.parent.content.size) {
              return false;
            }

            // Handle Enter at the end of a list item
            if ($from.parent.type.name === "listItem") {
              // If the list item is empty, exit the list
              if ($from.parent.content.size === 0) {
                view.dispatch(
                  state.tr
                    .lift($from.blockRange($from))
                    .split($from.pos)
                    .scrollIntoView()
                );
                return true;
              }

              // Otherwise, create a new list item
              view.dispatch(state.tr.split($from.pos, 2).scrollIntoView());
              return true;
            }
          }
          return false;
        },
      },
    },
  });

  useEffect(() => {
    if (editor && description) {
      if (editor.getHTML() !== description) {
        editor.commands.setContent(description);
      }
    }
  }, [description, editor]);

  // Link Modal Functions
  const openLinkModal = () => {
    const previousUrl = editor.getAttributes("link").href;
    setLinkURL(previousUrl || "");
    setShowLinkModal(true);
  };

  const closeLinkModal = () => {
    setShowLinkModal(false);
    setLinkURL("");
  };

  const updateLink = () => {
    if (linkURL === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: linkURL })
        .run();
    }
    closeLinkModal();
  };

  // Image Modal Functions
  const openImageModal = () => {
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedFiles([]);
  };

  const handleInputClick = () => {
    inputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles([...selectedFiles, ...files]);
  };

  const handleDelete = async (index) => {
    try {
      const fileToDelete = selectedFiles[index];

      if (typeof fileToDelete === "string") {
        await deleteImage(fileToDelete);
        toast.success("Image deleted successfully");
      }

      const newFiles = selectedFiles.filter((_, i) => i !== index);
      setSelectedFiles(newFiles);

      editor.commands.deleteSelection();
    } catch (err) {
      toast.error(err.message || "Failed to delete image");
    }
  };

  const handleUpload = async () => {
    if (!selectedFiles.length) {
      toast.error("Please select at least one file");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => {
        formData.append("images", file);
      });

      const response = await uploadImages(formData);

      if (response?.success && response?.images?.length) {
        toast.success("Images uploaded successfully!");

        response.images.forEach((img) => {
          const { state } = editor.view;
          const { tr } = state;

          const imageNode = state.schema.nodes.image.create({
            src: img.imageUrl,
          });

          tr.replaceSelectionWith(imageNode);

          let newPos = tr.selection.$to.pos + 1;
          if (newPos > tr.doc.content.size) {
            const paragraph = state.schema.nodes.paragraph.create();
            tr.insert(tr.doc.content.size, paragraph);
            newPos = tr.doc.content.size;
          }

          tr.setSelection(TextSelection.create(tr.doc, newPos));
          editor.view.dispatch(tr);
        });

        setSelectedFiles([]);
        closeImageModal();
      } else {
        throw new Error("Image upload failed");
      }
    } catch (err) {
      toast.error(err?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const addTable = () => {
    editor
      .chain()
      .focus()
      .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
      .run();
  };

  const colors = [
    "#000000",
    "#e60000",
    "#ff9900",
    "#ffff00",
    "#008a00",
    "#0066cc",
    "#9933ff",
    "#ffffff",
    "#facccc",
    "#ffebcc",
    "#ffffcc",
    "#cce8cc",
    "#cce0f5",
    "#ebd6ff",
    "#bbbbbb",
    "#f06666",
    "#ffc266",
    "#ffff66",
    "#66b266",
    "#66a3e0",
    "#c285ff",
    "#888888",
    "#a10000",
    "#b26b00",
    "#b2b200",
    "#006100",
    "#0047b2",
    "#6b24b2",
    "#444444",
    "#5c0000",
    "#663d00",
    "#666600",
    "#003700",
    "#002966",
    "#3d1466",
  ];

  if (!editor) return null;

  return (
    <div className="flex flex-col space-y-2">
      <span className="text-gray-800 font-semibold text-base">Description</span>

      <div className="w-full bg-white border border-gray-300 rounded-xl overflow-hidden">
        <div className="bg-gray-50 border-b border-gray-200 p-4">
          <div className="flex flex-wrap gap-2 items-center">
            {/* Undo/Redo */}
            <div className="flex gap-1 border-r border-gray-300 pr-2 mr-2">
              <button
                onClick={() => editor.chain().focus().undo().run()}
                disabled={!editor.can().undo()}
                className="p-2 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Undo"
              >
                <Undo2 size={16} />
              </button>
              <button
                onClick={() => editor.chain().focus().redo().run()}
                disabled={!editor.can().redo()}
                className="p-2 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Redo"
              >
                <Redo2 size={16} />
              </button>
            </div>

            {/* Text Style */}
            <div className="flex items-center gap-1 border-r border-gray-300 pr-2 mr-2">
              <select
                onChange={(e) => {
                  const level = parseInt(e.target.value);
                  if (level === 0) {
                    editor.chain().focus().setParagraph().run();
                  } else {
                    editor.chain().focus().toggleHeading({ level }).run();
                  }
                }}
                className="text-sm border border-gray-300 rounded px-2 py-1 bg-white"
                value={
                  editor.isActive("heading", { level: 1 })
                    ? "1"
                    : editor.isActive("heading", { level: 2 })
                    ? "2"
                    : editor.isActive("heading", { level: 3 })
                    ? "3"
                    : "0"
                }
              >
                <option value="0">Normal</option>
                <option value="1">Heading 1</option>
                <option value="2">Heading 2</option>
                <option value="3">Heading 3</option>
              </select>
            </div>

            {/* Basic Formatting */}
            <div className="flex gap-1 border-r border-gray-300 pr-2 mr-2">
              <button
                onClick={() => editor.chain().focus().toggleBold().run()}
                className={`p-2 rounded hover:bg-gray-200 ${
                  editor.isActive("bold") ? "bg-blue-100 text-blue-600" : ""
                }`}
                title="Bold"
              >
                <Bold size={16} />
              </button>
              <button
                onClick={() => editor.chain().focus().toggleItalic().run()}
                className={`p-2 rounded hover:bg-gray-200 ${
                  editor.isActive("italic") ? "bg-blue-100 text-blue-600" : ""
                }`}
                title="Italic"
              >
                <Italic size={16} />
              </button>
              <button
                onClick={() => editor.chain().focus().toggleUnderline().run()}
                className={`p-2 rounded hover:bg-gray-200 ${
                  editor.isActive("underline")
                    ? "bg-blue-100 text-blue-600"
                    : ""
                }`}
                title="Underline"
              >
                <UnderlineIcon size={16} />
              </button>
              <button
                onClick={() => editor.chain().focus().toggleStrike().run()}
                className={`p-2 rounded hover:bg-gray-200 ${
                  editor.isActive("strike") ? "bg-blue-100 text-blue-600" : ""
                }`}
                title="Strikethrough"
              >
                <Strikethrough size={16} />
              </button>
            </div>

            {/* Superscript/Subscript */}
            <div className="flex gap-1 border-r border-gray-300 pr-2 mr-2">
              <button
                onClick={() => editor.chain().focus().toggleSuperscript().run()}
                className={`p-2 rounded hover:bg-gray-200 ${
                  editor.isActive("superscript")
                    ? "bg-blue-100 text-blue-600"
                    : ""
                }`}
                title="Superscript"
              >
                <SuperscriptIcon size={16} />
              </button>
              <button
                onClick={() => editor.chain().focus().toggleSubscript().run()}
                className={`p-2 rounded hover:bg-gray-200 ${
                  editor.isActive("subscript")
                    ? "bg-blue-100 text-blue-600"
                    : ""
                }`}
                title="Subscript"
              >
                <SubscriptIcon size={16} />
              </button>
            </div>

            {/* Code */}
            <div className="flex gap-1 border-r border-gray-300 pr-2 mr-2">
              <button
                onClick={() => editor.chain().focus().toggleCode().run()}
                className={`p-2 rounded hover:bg-gray-200 ${
                  editor.isActive("code") ? "bg-blue-100 text-blue-600" : ""
                }`}
                title="Inline Code"
              >
                <CodeIcon size={16} />
              </button>
              <button
                onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                className={`p-2 rounded hover:bg-gray-200 ${
                  editor.isActive("codeBlock")
                    ? "bg-blue-100 text-blue-600"
                    : ""
                }`}
                title="Code Block"
              >
                <FileCode size={16} />
              </button>
            </div>

            {/* Lists */}
            <div className="flex gap-1 border-r border-gray-300 pr-2 mr-2">
              <button
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                className={`p-2 rounded hover:bg-gray-200 ${
                  editor.isActive("bulletList")
                    ? "bg-blue-100 text-blue-600"
                    : ""
                }`}
                title="Bullet List"
              >
                <List size={16} />
              </button>
              <button
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                className={`p-2 rounded hover:bg-gray-200 ${
                  editor.isActive("orderedList")
                    ? "bg-blue-100 text-blue-600"
                    : ""
                }`}
                title="Numbered List"
              >
                <ListOrdered size={16} />
              </button>
            </div>

            {/* Alignment */}
            <div className="flex gap-1 border-r border-gray-300 pr-2 mr-2">
              <button
                onClick={() =>
                  editor.chain().focus().setTextAlign("left").run()
                }
                className={`p-2 rounded hover:bg-gray-200 ${
                  editor.isActive({ textAlign: "left" })
                    ? "bg-blue-100 text-blue-600"
                    : ""
                }`}
                title="Align Left"
              >
                <AlignLeft size={16} />
              </button>
              <button
                onClick={() =>
                  editor.chain().focus().setTextAlign("center").run()
                }
                className={`p-2 rounded hover:bg-gray-200 ${
                  editor.isActive({ textAlign: "center" })
                    ? "bg-blue-100 text-blue-600"
                    : ""
                }`}
                title="Align Center"
              >
                <AlignCenter size={16} />
              </button>
              <button
                onClick={() =>
                  editor.chain().focus().setTextAlign("right").run()
                }
                className={`p-2 rounded hover:bg-gray-200 ${
                  editor.isActive({ textAlign: "right" })
                    ? "bg-blue-100 text-blue-600"
                    : ""
                }`}
                title="Align Right"
              >
                <AlignRight size={16} />
              </button>
              <button
                onClick={() =>
                  editor.chain().focus().setTextAlign("justify").run()
                }
                className={`p-2 rounded hover:bg-gray-200 ${
                  editor.isActive({ textAlign: "justify" })
                    ? "bg-blue-100 text-blue-600"
                    : ""
                }`}
                title="Justify"
              >
                <AlignJustify size={16} />
              </button>
            </div>

            {/* Color Picker */}
            <div className="flex gap-1 border-r border-gray-300 pr-2 mr-2 relative">
              <button
                onClick={() => setShowColorPicker(!showColorPicker)}
                className="p-2 rounded hover:bg-gray-200 flex items-center gap-1"
                title="Text Color"
              >
                <Type size={16} />
                <div
                  className="w-4 h-1 rounded"
                  style={{
                    backgroundColor:
                      editor.getAttributes("textStyle").color || "#000000",
                  }}
                />
              </button>

              {showColorPicker && (
                <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-2 z-10">
                  <div className="grid grid-cols-7 gap-1 w-48">
                    {colors.map((color) => (
                      <button
                        key={color}
                        onClick={() => {
                          editor.chain().focus().setColor(color).run();
                          setShowColorPicker(false);
                        }}
                        className="w-6 h-6 rounded border border-gray-300 hover:scale-110 transition-transform"
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))}
                  </div>
                  <button
                    onClick={() => {
                      editor.chain().focus().unsetColor().run();
                      setShowColorPicker(false);
                    }}
                    className="mt-2 w-full text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded"
                  >
                    Remove Color
                  </button>
                </div>
              )}
            </div>

            {/* Link & Media */}
            <div className="flex gap-1 border-r border-gray-300 pr-2 mr-2">
              <button
                onClick={openLinkModal}
                className={`p-2 rounded hover:bg-gray-200 ${
                  editor.isActive("link") ? "bg-blue-100 text-blue-600" : ""
                }`}
                title="Add Link"
              >
                <LinkIcon size={16} />
              </button>
              <button
                onClick={openImageModal}
                className="p-2 rounded hover:bg-gray-200"
                title="Add Image"
              >
                <ImageIcon size={16} />
              </button>
            </div>

            {/* Table & Extras */}
            <div className="flex gap-1">
              <button
                onClick={addTable}
                className="p-2 rounded hover:bg-gray-200"
                title="Insert Table"
              >
                <TableIcon size={16} />
              </button>
              <button
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
                className={`p-2 rounded hover:bg-gray-200 ${
                  editor.isActive("blockquote")
                    ? "bg-blue-100 text-blue-600"
                    : ""
                }`}
                title="Quote"
              >
                <Quote size={16} />
              </button>
              <button
                onClick={() => editor.chain().focus().setHorizontalRule().run()}
                className="p-2 rounded hover:bg-gray-200"
                title="Horizontal Rule"
              >
                <Minus size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Editor Content */}
        <div className="p-6">
          <EditorContent
            editor={editor}
            className="min-h-[300px] focus-within:outline-none"
          />
        </div>
      </div>

      {/* Modals */}
      <BlogModal
        closeModal={closeLinkModal}
        showModal={showLinkModal}
        setLinkURL={setLinkURL}
        linkURL={linkURL}
        updateLink={updateLink}
      />

      <BlogImagePopup
        closeModal={closeImageModal}
        showModal={showImageModal}
        inputRef={inputRef}
        handleFileChange={handleFileChange}
        handleInputClick={handleInputClick}
        selectedFiles={selectedFiles}
        handleDelete={handleDelete}
        handleUpload={handleUpload}
        uploading={uploading}
      />

      {/* Custom Styles */}
      <style jsx>{`
        .ProseMirror {
          outline: none;
        }

        .ProseMirror ul.prose-bullet-list {
          list-style-type: none;
          margin-left: 1.5rem;
          padding-left: 0;
        }

        .ProseMirror ol.prose-ordered-list {
          list-style-type: none;
          margin-left: 1.5rem;
          padding-left: 0;
        }

        .ProseMirror ul.prose-bullet-list li {
          display: list-item;
          margin: 0.5rem 0;
        }

        .ProseMirror ol.prose-ordered-list li {
          display: list-item;
          margin: 0.5rem 0;
        }

        .ProseMirror h1 {
          font-size: 2rem;
          font-weight: bold;
          margin: 1rem 0;
        }

        .ProseMirror h2 {
          font-size: 1.5rem;
          font-weight: bold;
          margin: 0.875rem 0;
        }

        .ProseMirror h3 {
          font-size: 1.25rem;
          font-weight: bold;
          margin: 0.75rem 0;
        }

        .ProseMirror p {
          margin: 0.5rem 0;
        }

        .ProseMirror code {
          background-color: #f3f4f6;
          padding: 0.125rem 0.25rem;
          border-radius: 0.25rem;
          font-family: "Courier New", monospace;
        }

        .ProseMirror pre {
          background-color: #1f2937;
          color: #f9fafb;
          padding: 1rem;
          border-radius: 0.5rem;
          overflow-x: auto;
          margin: 1rem 0;
        }

        .ProseMirror pre code {
          background: none;
          padding: 0;
          color: inherit;
        }

        .ProseMirror blockquote {
          border-left: 4px solid #e5e7eb;
          padding-left: 1rem;
          margin: 1rem 0;
          font-style: italic;
          color: #6b7280;
        }

        .ProseMirror hr {
          border: none;
          border-top: 2px solid #e5e7eb;
          margin: 2rem 0;
        }

        .ProseMirror table {
          border-collapse: collapse;
          table-layout: fixed;
          width: 100%;
          margin: 1rem 0;
          overflow: hidden;
        }

        .ProseMirror table td,
        .ProseMirror table th {
          min-width: 1rem;
          border: 2px solid #d1d5db;
          padding: 0.5rem;
          vertical-align: top;
          box-sizing: border-box;
          position: relative;
        }

        .ProseMirror table th {
          font-weight: bold;
          text-align: left;
          background-color: #f9fafb;
        }

        .ProseMirror .list-item {
          position: relative;
          padding-left: 1.5rem;
        }

        .ProseMirror .list-item::before {
          position: absolute;
          left: 0;
        }

        .ProseMirror ul.prose-bullet-list .list-item::before {
          content: "•";
        }

        .ProseMirror ol.prose-ordered-list .list-item::before {
          content: counter(list-item) ".";
          counter-increment: list-item;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
